// Apolo IDE — TaskPanel (S2 Task Layer E3-WS1-T01).
//
// Full decompose-to-DAG flow for the SidePanel 'dag' tab:
//   idle        → textarea + "Decompor" button
//   planning    → spinner
//   awaiting-approval → TaskDagBoard + Aprovar/Rejeitar
//   approving   → spinner
//   approved    → success banner + DAG (frozen)
//   rejected    → rejected banner + reset
//   error       → error + reset
//
// The panel is intentionally self-contained: it owns its own useTaskPanel
// instance so it doesn't couple to the rest of SessionWorkspace state.
// LAWS obeyed:
//   - Board = DAG, not Kanban (uses TaskDagBoard, not a list)
//   - No task executes without explicit user approval (Aprovar button)
//   - Reuses /api/agents/decompose (no new endpoint)
//   - rx-* tokens, anti-glass surface
//   - modelRegistry zero-diff SACRED (untouched)

import { useCallback, useState } from 'react';
import { CheckCircle, Loader, Route, XCircle } from 'lucide-react';
import useTaskPanel from '../../../hooks/useTaskPanel.js';
import { TaskDagBoard } from './TaskDagBoard.jsx';

// ── Approve/Reject action bar ─────────────────────────────────────────────────

function ApprovalBar({ busy, onApprove, onReject, taskCount, budgetWarning }) {
  return (
    <div
      data-testid="task-approval-bar"
      style={{
        borderTop: '1px solid var(--rx-border)',
        padding: '12px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      {budgetWarning && (
        <p style={{ fontSize: 11, color: 'var(--rx-gold)', margin: 0 }}>
          ⚠ {budgetWarning}
        </p>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          data-testid="task-approve-btn"
          disabled={busy}
          onClick={onApprove}
          aria-label={`Aprovar ${taskCount} task${taskCount !== 1 ? 's' : ''}`}
          style={{
            flex: 1,
            padding: '7px 12px',
            borderRadius: 8,
            border: 'none',
            background: 'var(--rx-accent)',
            color: '#fff',
            fontSize: 12,
            fontWeight: 600,
            cursor: busy ? 'not-allowed' : 'pointer',
            opacity: busy ? 0.6 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
          }}
        >
          {busy ? (
            <Loader size={12} aria-hidden="true" style={{ animation: 'spin 1.2s linear infinite' }} />
          ) : (
            <CheckCircle size={12} aria-hidden="true" />
          )}
          Aprovar ({taskCount})
        </button>
        <button
          type="button"
          data-testid="task-reject-btn"
          disabled={busy}
          onClick={onReject}
          aria-label="Rejeitar plano"
          style={{
            padding: '7px 14px',
            borderRadius: 8,
            border: '1px solid var(--rx-del)',
            background: 'transparent',
            color: 'var(--rx-del)',
            fontSize: 12,
            fontWeight: 600,
            cursor: busy ? 'not-allowed' : 'pointer',
            opacity: busy ? 0.6 : 1,
          }}
        >
          Rejeitar
        </button>
      </div>
    </div>
  );
}

// ── TaskPanel ─────────────────────────────────────────────────────────────────

/**
 * @param {{ sessionId?: string }} props
 */
export default function TaskPanel({ sessionId }) {
  const panel = useTaskPanel({ sessionId });
  const [message, setMessage] = useState('');

  const onDecompose = useCallback(
    async (e) => {
      e.preventDefault();
      if (message.trim().length < 10) return;
      await panel.decompose({ message: message.trim(), sessionId });
      setMessage('');
    },
    [panel, message, sessionId],
  );

  const onReset = useCallback(() => {
    panel.reset();
    setMessage('');
  }, [panel]);

  const isIdle = panel.phase === 'idle';
  const isPlanning = panel.phase === 'planning';
  const isAwaiting = panel.phase === 'awaiting-approval';
  const isApproving = panel.phase === 'approving';
  const isApproved = panel.phase === 'approved';
  const isRejected = panel.phase === 'rejected';
  const isError = panel.phase === 'error';

  return (
    <div
      data-testid="task-panel"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: 'var(--rx-s1)',
        color: 'var(--rx-tx1)',
      }}
    >
      {/* ── Header ── */}
      <header
        style={{
          padding: '12px 16px 10px',
          borderBottom: '1px solid var(--rx-border)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <Route size={14} aria-hidden="true" style={{ color: 'var(--rx-accent)', flexShrink: 0 }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--rx-tx1)' }}>
          Decompose & Run
        </span>
        {panel.tasks.length > 0 && (
          <span
            style={{
              marginLeft: 'auto',
              fontSize: 10,
              color: 'var(--rx-accent)',
              background: 'rgba(59,130,246,.12)',
              border: '1px solid rgba(59,130,246,.25)',
              borderRadius: 999,
              padding: '2px 8px',
            }}
          >
            {panel.tasks.length} task{panel.tasks.length !== 1 ? 's' : ''}
          </span>
        )}
      </header>

      {/* ── Body ── */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

        {/* Input form — shown when idle */}
        {(isIdle || isError) && (
          <form
            onSubmit={onDecompose}
            style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '16px' }}
          >
            <label
              htmlFor="task-panel-message"
              style={{ fontSize: 10, color: 'var(--rx-tx3)', letterSpacing: '0.05em', textTransform: 'uppercase' }}
            >
              Meta complexa → Decomposer vai quebrar em tasks
            </label>
            <textarea
              id="task-panel-message"
              data-testid="task-panel-input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder="Ex: implementa endpoint /api/foo + testes + docs..."
              style={{
                resize: 'vertical',
                borderRadius: 8,
                border: '1px solid var(--rx-border)',
                background: 'var(--rx-s2)',
                padding: '8px 10px',
                fontSize: 12,
                color: 'var(--rx-tx1)',
                outline: 'none',
              }}
            />
            {panel.error && (
              <p
                role="alert"
                data-testid="task-panel-error"
                style={{ fontSize: 11, color: 'var(--rx-del)', margin: 0 }}
              >
                {panel.error}
              </p>
            )}
            <button
              type="submit"
              data-testid="task-decompose-btn"
              disabled={panel.busy || message.trim().length < 10}
              style={{
                alignSelf: 'flex-end',
                padding: '6px 14px',
                borderRadius: 8,
                border: 'none',
                background: 'var(--rx-accent)',
                color: '#fff',
                fontSize: 12,
                fontWeight: 600,
                cursor: panel.busy || message.trim().length < 10 ? 'not-allowed' : 'pointer',
                opacity: panel.busy || message.trim().length < 10 ? 0.5 : 1,
              }}
            >
              Decompor
            </button>
          </form>
        )}

        {/* Planning spinner */}
        {isPlanning && (
          <div
            data-testid="task-panel-planning"
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '24px 16px', color: 'var(--rx-tx2)' }}
          >
            <Loader size={16} aria-hidden="true" style={{ animation: 'spin 1.2s linear infinite', color: 'var(--rx-accent)' }} />
            <span style={{ fontSize: 13 }}>Decompondo meta em tasks…</span>
          </div>
        )}

        {/* DAG board — awaiting approval */}
        {(isAwaiting || isApproving || isApproved) && (
          <div style={{ flex: 1, overflowX: 'auto', overflowY: 'hidden', padding: '12px 8px 0' }}>
            <TaskDagBoard
              tasks={panel.tasks}
              verifyResults={panel.verifyResults}
              selectedTaskId={panel.selectedTaskId}
              onTaskClick={(t) => panel.setSelectedTaskId(t.id === panel.selectedTaskId ? null : t.id)}
              style={{ minHeight: 140 }}
            />
          </div>
        )}

        {/* Success banner */}
        {isApproved && (
          <div
            data-testid="task-panel-approved"
            style={{
              margin: '12px 16px',
              padding: '10px 14px',
              borderRadius: 8,
              border: '1px solid var(--rx-add)',
              background: 'rgba(67,224,138,.08)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 12,
              color: 'var(--rx-add)',
            }}
          >
            <CheckCircle size={14} aria-hidden="true" />
            Plano aprovado — dispatch em andamento.
            <button
              type="button"
              onClick={onReset}
              style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--rx-tx3)', background: 'none', border: 'none', cursor: 'pointer' }}
            >
              Novo
            </button>
          </div>
        )}

        {/* Rejected banner */}
        {isRejected && (
          <div
            data-testid="task-panel-rejected"
            style={{
              margin: '16px',
              padding: '10px 14px',
              borderRadius: 8,
              border: '1px solid var(--rx-del)',
              background: 'rgba(255,122,122,.08)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 12,
              color: 'var(--rx-del)',
            }}
          >
            <XCircle size={14} aria-hidden="true" />
            Plano rejeitado.
            <button
              type="button"
              onClick={onReset}
              data-testid="task-panel-reset"
              style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--rx-accent)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
            >
              Tentar novamente
            </button>
          </div>
        )}
      </div>

      {/* ── Approval bar — pinned to bottom when awaiting ── */}
      {(isAwaiting || isApproving) && (
        <ApprovalBar
          busy={isApproving}
          onApprove={panel.approve}
          onReject={panel.reject}
          taskCount={panel.tasks.length}
          budgetWarning={panel.budget?.warning}
        />
      )}
    </div>
  );
}
